import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  fetchAssetDetail,
  clearSearch,
  setActiveSection,
  executeSearch,
} from '@/features/search/searchSlice';
import { setPlaying, updateCurrentTime, requestSeek } from '@/features/player/playerSlice';
import type { AssetCard, MatchLabel, SectionSlug } from '@/types';
import styles from './SearchResults.module.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function msToDisplay(ms: number | null): string {
  if (!ms) return '';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function labelClass(label: MatchLabel | undefined): string {
  if (!label) return '';
  const map: Record<MatchLabel, string> = {
    Exact: styles.badgeExact,
    Strong: styles.badgeStrong,
    Moderate: styles.badgeModerate,
  };
  return map[label] ?? '';
}

function cleanName(fileName: string): string {
  return fileName.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
}

// ─── Skeleton card ────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className={styles.skeletonCard} aria-hidden="true">
      <div className={styles.skeletonThumb} />
      <div className={styles.skeletonLine} style={{ width: '80%' }} />
      <div className={styles.skeletonLine} style={{ width: '50%' }} />
    </div>
  );
}

// ─── Result card ──────────────────────────────────────────────────────────────

interface ResultCardProps {
  card: AssetCard;
  isActive: boolean;
  isLoadingThis: boolean;
  onSelect: (card: AssetCard) => void;
}

function ResultCard({ card, isActive, isLoadingThis, onSelect }: ResultCardProps) {
  const duration = msToDisplay(card.duration_ms);

  return (
    <button
      className={`${styles.card} ${isActive ? styles.cardActive : ''} ${isLoadingThis ? styles.cardPending : ''}`}
      onClick={() => onSelect(card)}
      aria-label={`${cleanName(card.file_name)}${card.match_label ? `, ${card.match_label} match` : ''}`}
      aria-pressed={isActive}
    >
      {/* Thumbnail */}
      <div className={styles.thumbWrap}>
        {card.thumbnail_url ? (
          <img
            className={styles.thumb}
            src={card.thumbnail_url}
            alt=""
            loading="lazy"
            aria-hidden="true"
          />
        ) : (
          <div className={styles.thumbFallback} aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" opacity="0.4">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        )}

        {/* Duration badge */}
        {duration && <span className={styles.duration}>{duration}</span>}

        {/* Spinner on the card being fetched */}
        {isLoadingThis && (
          <div className={styles.loadingOverlay} aria-label="Loading asset detail">
            <div className={styles.spinner} />
          </div>
        )}

        {/* Checkmark only once loaded and active */}
        {isActive && !isLoadingThis && (
          <div className={styles.activeOverlay} aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
        )}
      </div>

      {/* Info */}
      <div className={styles.info}>
        <span className={styles.name}>{cleanName(card.file_name)}</span>
        <div className={styles.badgeRow}>
          {card.match_label && (
            <span className={`${styles.badge} ${labelClass(card.match_label)}`}>
              {card.match_label}
            </span>
          )}
          {card.best_match?.type === 'field_match' && (
            <span className={styles.matchedIn}>
              {card.best_match.field}: {card.best_match.content}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SearchResults() {
  const dispatch = useAppDispatch();
  const { query, results, total, loading, activeAsset, loadingAssetId, sections_summary, activeSection } =
    useAppSelector((s) => s.search);

  // Only render when there's a meaningful query
  if (query.length < 2) return null;

  const handleSelect = (card: AssetCard) => {
    dispatch(setPlaying(false));
    dispatch(updateCurrentTime(0));

    if (card.best_match && card.best_match.type !== 'field_match') {
      dispatch(requestSeek(card.best_match.timestamp_ms / 1000));
    }
    // best_match null / field_match → no seek, plays from 0

    dispatch(fetchAssetDetail({ assetId: card.asset_id, query }));
  };

  const handleClear = () => {
    dispatch(clearSearch());
  };

  const handleSectionFilter = (slug: SectionSlug | null) => {
    dispatch(setActiveSection(slug));
    dispatch(executeSearch({ query, limit: 15 }));
  };

  const activeClipCount = activeAsset?.matched_clips?.length ?? activeAsset?.matched_segments?.length ?? 0;

  return (
    <section className={styles.section} aria-label="Search results">
      {/* Header row */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {loading ? (
            <span className={styles.headerLabel}>Searching…</span>
          ) : (
            <span className={styles.headerLabel}>
              <b>{total}</b> result{total !== 1 ? 's' : ''} for{' '}
              <span className={styles.headerQuery}>"{query}"</span>
            </span>
          )}
          {activeAsset && !loading && (
            <span className={styles.headerNow}>
              · Now showing: <b>{cleanName(activeAsset.file_name)}</b>
            </span>
          )}
        </div>
        <button className={styles.clearBtn} onClick={handleClear} aria-label="Clear search">
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M1 1l12 12M13 1L1 13" />
          </svg>
          Clear
        </button>
      </div>

      {/* Section filter chips */}
      {sections_summary.length > 0 && (
        <div className={styles.filterChips} role="group" aria-label="Filter by section">
          {sections_summary.map((s) => (
            <button
              key={s.slug}
              className={`${styles.chip} ${activeSection === s.slug ? styles.chipActive : ''}`}
              onClick={() => handleSectionFilter(activeSection === s.slug ? null : s.slug as SectionSlug)}
              aria-pressed={activeSection === s.slug}
            >
              {s.label}
              <span className={styles.chipCount}>{s.match_count}</span>
            </button>
          ))}
          {activeSection && (
            <button
              className={styles.chipClear}
              onClick={() => handleSectionFilter(null)}
              aria-label="Clear section filter"
            >
              ✕ All
            </button>
          )}
        </div>
      )}

      {/* Cards row — horizontal scroll */}
      <div className={styles.row} role="list">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
          : results.map((card) => (
              <ResultCard
                key={card.asset_id}
                card={card}
                isActive={activeAsset?.asset_id === card.asset_id && !loadingAssetId}
                isLoadingThis={loadingAssetId === card.asset_id}
                onSelect={handleSelect}
              />
            ))}
      </div>

      {/* Matched clips / segments hint */}
      {activeAsset && !loadingAssetId && activeClipCount > 0 && (
        <div className={styles.segmentsHint}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4l3 3" />
          </svg>
          {activeClipCount} matched clip
          {activeClipCount !== 1 ? 's' : ''} found — click transcript lines to jump
        </div>
      )}
    </section>
  );
}
